import React, { useState } from 'react';

import { dateTime, GrafanaTheme2, TimeRange } from '@grafana/data';
import { Alert, Button, Field, FieldValidationMessage, Icon, Input, Tooltip, useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';
import { Labels, LabelValidation } from 'grafana-ml-common/components/Labels';

import { adminApiPost } from 'api';
import { getSiftPreview } from 'api/endpoints/sift';
import { useFeature } from 'hooks';
import useRudderStack, { RudderstackEvents } from 'hooks/useRudderstack';
import { createInvestigationRequestFromFormModel } from 'utils';
import { queryClient } from 'utils/queryClient';

import { DurationDisplay } from '../../components/DurationDisplay';
import { Services, ServiceValidation } from '../../components/ServicesForm';
import {
  Input as InputType,
  InvestigationFormModel,
  InvestigationPreview,
  InvestigationSourceType,
  Label,
  LabelRecommendationResponse,
  NewInvestigation,
  Service,
} from '../../types';
import { PreviewPanel } from './Components/PreviewPanel';
import { SiftTimePicker } from './SiftTimePicker';

// hook to track changes in inputs to prevent re-running the preview call when it is not needed
const usePreviewInputChanged = (initialStart: string, initialEnd: string, initialLabels: InputType[]) => {
  const [previewInputs, setPreviewInputs] = useState({
    start: initialStart,
    end: initialEnd,
    labels: JSON.stringify(initialLabels),
  });

  const haveInputsChanged = (start: string, end: string, labels: InputType[]) => {
    if (start !== previewInputs.start || end !== previewInputs.end || JSON.stringify(labels) !== previewInputs.labels) {
      setPreviewInputs({ start, end, labels: JSON.stringify(labels) });
      return true;
    }
    return false;
  };

  return haveInputsChanged;
};

export const defaultNewInvestigationForm = (): InvestigationFormModel => {
  const end = new Date();
  // 30 minutes, for consistency with the backend defaults.
  const start = new Date(end.getTime() - 30 * 60 * 1000);
  return {
    name: '',
    start: start.toISOString(),
    end: end.toISOString(),

    labels: [
      { name: 'cluster', value: '' },
      { name: 'namespace', value: '' },
    ],

    services: [],
    // Hidden on the form but sometimes populated by clues provided by extensions;
    // see overrideInputs in CreateInvestigation.tsx.
    queries: [],

    investigationSource: {
      type: InvestigationSourceType.ML,
      id: '',
      url: '',
    },
  };
};

interface RunSiftFormProps {
  defaults?: InvestigationFormModel;
  onSubmit: (form: NewInvestigation) => void;
  onClose?: () => void;
}

export default function RunSiftForm({ defaults, onClose, onSubmit }: RunSiftFormProps): React.ReactElement | null {
  const styles = useStyles2(getStyles);
  const trackRudderStackEvent = useRudderStack();
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewResults, setPreviewResults] = useState<InvestigationPreview | undefined>(undefined);
  const [isChanging, setIsChanging] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [formData, setFormData] = useState<InvestigationFormModel>(defaults ?? defaultNewInvestigationForm());
  const [showTimeDefaultMsg, setShowTimeDefaultMsg] = useState<boolean>(true);
  const showPreviewFeature = useFeature('SIFTPreview');
  const newInvestigation = createInvestigationRequestFromFormModel(formData);
  const [recommendationCalled, setRecommendationCalled] = useState<boolean>(false);
  const inputsChanged = usePreviewInputChanged(
    newInvestigation.requestData.start,
    newInvestigation.requestData.end,
    newInvestigation.inputs ?? []
  );
  const [hasRecommendedLabels, setHasRecommendedLabels] = useState<boolean>(false);

  const onFieldChange = (field: string, value: string | TimeRange | Label[] | Service[]) => {
    // time range is broken into start/end strings
    if (field === 'timeRange') {
      setFormData({
        ...formData,
        start: (value as TimeRange).from.toISOString(),
        end: (value as TimeRange).to.toISOString(),
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const startInvestigation = (form: InvestigationFormModel) => {
    const labelValidation = getLabelValidation(form.labels, undefined);
    const serviceValidation = getServiceValidation(form.services ?? []);
    if (
      labelValidation.valid === true &&
      formData.name !== '' &&
      formData.labels.length > 0 &&
      serviceValidation.valid === true
    ) {
      trackRudderStackEvent(RudderstackEvents.CreateInvestigation, {
        labelsEdited: JSON.stringify(defaults?.labels) !== JSON.stringify(form.labels),
        labels: form.labels.length,
        labelsRecommended: defaults?.labels.length,
        timeRangeEdited: defaults?.start !== form.start || defaults?.end !== form.end,
        investigationSourceType: form.investigationSource.type,
        services: form.services?.length,
      });
      onSubmit(createInvestigationRequestFromFormModel(form));
    } else {
      setShowErrors(true);
    }
  };

  const getPreviewValidation = (labels: Label[], preview: InvestigationPreview | undefined) =>
    labels.reduce((result, item) => {
      const message =
        preview?.labels[item.name]?.included === false ? 'Label will not be used in the investigation' : '';

      return [...result, message];
    }, [] as string[]);

  // validate if labels meet requirements
  const getLabelValidation = (labels: Label[], preview: InvestigationPreview | undefined) =>
    labels.reduce(
      (result, item, i) => {
        const message =
          preview?.labels[item.name]?.included === false
            ? 'Label will not be used in the investigation'
            : labels.filter(({ name }, j) => name === item.name && i !== j).length > 0
              ? 'Duplicate labels are not allowed'
              : item.name === ''
                ? 'Label names cannot be empty'
                : item.value === ''
                  ? 'Label value cannot be empty'
                  : '';

        return {
          valid: result.valid === false ? false : message === '',
          messages: [...result.messages, message],
        };
      },
      { valid: true, messages: [] } as LabelValidation
    );

  // validate if service inputs meet requirements
  const getServiceValidation = (services: Service[]) =>
    services.reduce(
      (result, item, i) => {
        const message =
          services.filter(({ name, namespace }, j) => name === item.name && namespace === item.namespace && i !== j)
            .length > 0
            ? 'Duplicate services are not allowed'
            : item.name === ''
              ? 'Service name cannot be empty'
              : '';

        return {
          valid: result.valid === false ? false : message === '',
          messages: [...result.messages, message],
        };
      },
      { valid: true, messages: [] } as ServiceValidation
    );

  // get Now
  const now = new Date();
  // get default start time if provided start time is in the future
  const defaultStart = new Date(now.getTime() - 30 * 60 * 1000);
  // get provided start and end times in milliseconds
  const startMs = new Date(formData.start).getTime();
  const endMs = new Date(formData.end).getTime();
  // determine if the provided start and end times are in the future
  const timeRangeInFuture = startMs > now.getTime() || endMs > now.getTime();

  // set start and end times, using defaults when time range is in the future
  const startValue = timeRangeInFuture ? defaultStart.toISOString() : formData.start;
  const endValue = timeRangeInFuture ? now.toISOString() : formData.end;

  const timeRangeValue: TimeRange = {
    from: dateTime(startValue),
    to: dateTime(endValue),
    raw: { from: startValue, to: endValue },
  };

  // has empty label?
  const hasInvalidValues =
    formData.labels.filter((v) => v.value === '' || v.value === undefined || v.value === null).length > 0 ||
    (formData.services ?? []).filter((v) => v.namespace === '' || v.namespace === undefined || v.namespace === null)
      .length > 0;

  // at least one label is required for the preview
  const hasLabel =
    formData.labels.filter((v) => v.value !== '' && v.value !== undefined && v.value !== null).length > 0;

  const haveInputsChanged = !isChanging
    ? inputsChanged(newInvestigation.requestData.start, newInvestigation.requestData.end, [
        ...(newInvestigation.inputs as InputType[]),
      ])
    : false;

  // if changes are done, and we have labels, lets run the preview call here
  if (
    haveInputsChanged &&
    showPreviewFeature &&
    isPreviewLoading === false &&
    !isChanging &&
    !hasInvalidValues &&
    hasLabel
  ) {
    setIsPreviewLoading(true);
    queryClient
      .fetchQuery('sift-preview', () => getSiftPreview(newInvestigation))
      .then((result) => {
        setPreviewResults(result);
        setIsPreviewLoading(false);
      });
  }

  const setChangingDone = () => {
    setIsChanging(false);
    setIsPreviewLoading(false);

    // clear preview results if inputs have changed
    if (haveInputsChanged) {
      setPreviewResults(undefined);
    }

    queryClient.cancelQueries({ queryKey: ['sift-preview'] });
  };

  const handleRecommendLabels = async () => {
    if (!recommendationCalled) {
      setIsChanging(true);
      try {
        const response = await adminApiPost<LabelRecommendationResponse>('label-recommendation/api/v0/recommend/', {
          data: {
            maxLabels: 3,
            maxValues: 4,
          },
        });

        if (response.data.data) {
          // Convert recommended labels to the Label format

          const recommendedLabels: Label[] = response.data.data.labels.map((labelName) => ({
            name: labelName,
            value: response.data.data.values[labelName][0] ?? '',
            options: response.data.data.values[labelName] ?? [],
          }));

          if (recommendedLabels.length > 0) {
            onFieldChange('labels', recommendedLabels);
            setHasRecommendedLabels(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch label recommendation:', error);
        // You might want to add error handling/notification here
      } finally {
        setChangingDone();
      }
      setTimeout(() => setRecommendationCalled(true), 50);
    }
  };

  // if recommendation have not been called, and the form has no label values set
  // lookup recommendation
  const hasValues =
    formData.labels.filter((v) => v.value !== '' && v.value !== undefined && v.value !== null).length > 0;
  if (!recommendationCalled && !hasValues && !isChanging) {
    handleRecommendLabels();
  }

  return (
    <form id="investigation-form" className={styles.form} data-element="sift-create-investigation-form">
      <Field label="Investigation name">
        <Input
          name="investigation-name"
          value={formData.name}
          onKeyDownCapture={() => {
            setIsChanging(true);
          }}
          onBlur={() => {
            setChangingDone();
          }}
          onChange={(e: React.FormEvent<HTMLInputElement>) => onFieldChange('name', e.currentTarget.value)}
          autoFocus={true}
        />
      </Field>
      {showErrors && formData.name === '' ? (
        <FieldValidationMessage className={styles.error}>Name field cannot be empty</FieldValidationMessage>
      ) : null}
      <Field label="Labels">
        <>
          {hasRecommendedLabels && (
            <Alert title="Label Recommendation" severity="info" onRemove={() => setHasRecommendedLabels(false)}>
              <div>No labels were provided, recommended labels and values have been added to the form.</div>
              <div>Additional value recommendation available in dropdown.</div>
            </Alert>
          )}
          <Labels
            validation={getLabelValidation(formData.labels, previewResults)}
            previewMessage={getPreviewValidation(formData.labels, previewResults)}
            showErrors={showErrors}
            values={formData.labels}
            onChange={(labels) => {
              onFieldChange('labels', labels);
            }}
            onStartChange={() => {
              setIsChanging(true);
            }}
            onEndChange={() => {
              setChangingDone();
            }}
          />
        </>
      </Field>
      {showErrors && formData.labels.length === 0 && (
        <FieldValidationMessage className={styles.error}>At least one label must be provided</FieldValidationMessage>
      )}
      <Field
        label={
          <div className={styles.label}>
            Services
            <Tooltip content={'Investigate specific services using OpenTelemetry data (optional)'}>
              <div className={styles.info}>
                <Icon tabIndex={0} name="info-circle" size="sm" />
              </div>
            </Tooltip>
          </div>
        }
      >
        <Services
          validation={getServiceValidation(formData.services ?? [])}
          showErrors={showErrors}
          values={formData.services ?? []}
          onChange={(services) => {
            onFieldChange('services', services);
          }}
          onStartChange={() => {
            setIsChanging(true);
          }}
          onEndChange={() => {
            setChangingDone();
          }}
        />
      </Field>
      <Field label="Time range">
        <>
          {timeRangeInFuture && showTimeDefaultMsg ? (
            <Alert title="Time range defaulted" severity="info" onRemove={() => setShowTimeDefaultMsg(false)}>
              The provided time range is in the future and will not provide results. We have defaulted to the last 30
              minutes. Adjust the times below if needed.
            </Alert>
          ) : null}
          <SiftTimePicker value={timeRangeValue} onChange={(range) => onFieldChange('timeRange', range)} />
          <div className={styles.notes}>
            Selected investigation time range:
            <DurationDisplay startTime={formData.start} endTime={formData.end} />
          </div>
        </>
      </Field>

      {showPreviewFeature ? (
        <PreviewPanel
          isLoading={isPreviewLoading}
          showResults={!isChanging && !hasInvalidValues && hasLabel}
          previewResults={previewResults}
        />
      ) : null}
      <div className={styles.buttons} data-element="sift-form-buttons">
        <Button
          onClick={() => {
            if (typeof onClose === 'function') {
              onClose();
            }
          }}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            startInvestigation(formData);
          }}
        >
          Create
        </Button>
      </div>
    </form>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  return {
    form: css`
      min-height: 500px;
    `,
    optional: css`
      background-color: ${theme.colors.background.secondary};
      padding: 20px;
    `,
    notes: css`
      display: flex;
      gap: 8px;
      color: ${theme.colors.text.secondary};
      font-size: ${theme.typography.bodySmall.fontSize};
    `,
    buttons: css`
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 20px 0px;
    `,
    optionButton: css`
      margin-left: -10px;
    `,
    optionField: css`
      margin: 10px 0px;
    `,
    error: css`
      width: 100%;
      margin-bottom: 10px;
    `,
    info: css`
      display: flex;
      align-items: center;
      padding: 5px 10px 5px 5px;
      justify-content: space-between;
    `,
    label: css`
      display: flex;
      align-items: center;
      font-size: 12px;
      font-weight: 500;
    `,
    previewWrapper: css`
      background-color: ${theme.colors.background.secondary};
      padding: 20px;
    `,
  };
};
